// @ts-nocheck
/* eslint-disable */
import * as React from 'react';
import styled from 'styled-components';
import { Text } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import {
  breakpointsMap,
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_25,
} from '@oneaudi/unified-web-common';
import { messages } from '../../../i18n/messages';
import { getLocalizedWeekdayName, getLocalizedTime } from '../../../utils/dateUtils';
import type { DepartmentOpeningHours } from './PartnerData';
import { getDepartmentOpeningStatus, OpeningState } from './OpeningHoursCalculator';
import { camelize } from '../../../utils/camelize';
import { LayoutContainer } from './LayoutContainer';
import { useMedia } from '../../../hooks/useMedia';
import mapContext from '../../../contexts/map-context';

interface StatusIndicatorProps {
  theme?: {
    colors?: {
      base?: {
        signal: Record<string, string>;
      };
    };
  };
  color?: string;
}

const StatusIndicator = styled((props: Record<string, unknown>) => <Text tag="span" {...props} />)`
  border-radius: 9999px;
  width: 11px;
  height: 11px;
  line-height: 1;
  content: '';
  margin: 0 12px 3px 0;
  display: inline-block;
  vertical-align: middle;
  background-color: ${({ theme, color }: StatusIndicatorProps) =>
    color && theme?.colors?.base?.signal[color]
      ? `var(${theme.colors.base.signal[color]})`
      : color};
`;

export const OpeningHoursLabel = ({
  department,
  now,
  isNarrow,
}: {
  department: DepartmentOpeningHours;
  now: Date;
  isNarrow?: boolean;
}): React.JSX.Element => {
  const { locale } = mapContext.useMapState();
  const { language } = locale;
  const openingStatus = getDepartmentOpeningStatus(department, now);
  let openStatusLabel: React.ReactElement;
  let openStatusIndicator;

  const willOpenAtDayNameString =
    openingStatus.next?.day && getLocalizedWeekdayName(openingStatus.next.day, language);

  const willOpenAtTime =
    openingStatus.next?.openTime && getLocalizedTime(openingStatus.next.openTime, language);

  const isViewportM = useMedia(`only screen and (max-width: ${breakpointsMap.md})`);
  const spaceEnd = isViewportM || isNarrow ? 0 : '28px';

  switch (openingStatus.status) {
    case OpeningState.Open:
      openStatusLabel = (
        <Text tag="span">
          {useI18n(messages.openingHoursLabelOpenUntil)}
          {` ${
            openingStatus.current?.closeTime &&
            getLocalizedTime(openingStatus.current.closeTime, language)
          }`}
        </Text>
      );
      openStatusIndicator = <StatusIndicator spaceInlineEnd="s" color="#0da20d" />;
      break;
    case OpeningState.OpenClosingSoon:
      openStatusLabel = (
        <Text tag="span">
          {useI18n(messages.openingHoursLabelClosesSoon)}
          {` ${
            openingStatus.current?.closeTime &&
            getLocalizedTime(openingStatus.current.closeTime, language)
          }`}
        </Text>
      );
      openStatusIndicator = <StatusIndicator spaceInlineEnd="s" color="#ffaa00" />;
      break;
    case OpeningState.Closed:
      openStatusLabel = (
        <Text tag="span">
          {useI18n(messages.openingHoursLabelClosed)}
          {`, `}
          {useI18n(messages.openingHoursLabelWillOpenAt)}
          {` ${willOpenAtDayNameString} ${willOpenAtTime}`}
        </Text>
      );
      openStatusIndicator = <StatusIndicator spaceInlineEnd="s" color="#eb0d3f" />;
      break;
    default:
      openStatusIndicator = null;
      openStatusLabel = (
        <Text tag="span">{useI18n(messages.openingHoursLabelStatusIsUnknown)}</Text>
      );
      break;
  }
  return (
    <LayoutContainer
      style={{ marginBlockEnd: spaceEnd, display: 'grid', justifyItems: 'baseline' }}
    >
      <Text
        tag="p"
        variant={{ order: '1', style: 'bold', type: 'copy' }}
        margin={[SYS_SPACE_FIXED_25, 0, SYS_SPACE_FIXED_100, 0]}
        id={`openingHoursDepartment-${camelize(department.departmentName)}`}
      >
        {department.departmentName}
      </Text>
      <StyledText
        tag="span"
        id={`statusDepartment-${camelize(department.departmentName)}`}
        className="opening-hours-container"
      >
        {openStatusIndicator}
        {openStatusLabel}
      </StyledText>
    </LayoutContainer>
  );
};

const StyledText = styled(Text)`
  justify-self: baseline;
`;
